import { useEffect, useState, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Spinner,
  apiFetch,
  type PortalEnrollmentDetail,
  type PortalSignRequest,
} from "@dearbill/shared";
import { DocumentPreview } from "./sign/DocumentPreview";
import { SignatureForm, type SignatureInput } from "./sign/SignatureForm";
import { usePdfBlob } from "./sign/usePdfBlob";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const FORM_TITLE = "Medicare Prescription Payment Plan enrollment form (CMS-10882)";

interface PortalSignProps {
  /** Portal session bearer token. */
  session: string;
  /** The enrollment to sign: `latestEnrollment.id` from the dashboard payload. */
  enrollmentId: string;
  /** Called when the session is rejected (401). */
  onSignOut: () => void;
  /**
   * Called whenever the sign view is done with the member: a successful sign, a
   * load-gate bounce (already signed, not signable, or a 404), or a 409 or 422
   * on submit. The dashboard refetches on exit, so it reflects reality either way.
   */
  onExit: () => void;
}

/**
 * The in-dashboard CMS-10882 signing view.
 *
 * The portal session already authenticates the member, so there is no link
 * minting and no DOB challenge. The view:
 *   1. loads the enrollment detail. Only a signable, unsigned form may be
 *      signed here, so a signed or unsignable form, or a 404 for a form outside
 *      the session's scope, exits to the dashboard, which reflects reality;
 *   2. fetches the prefilled, unsigned PDF as an authenticated blob
 *      (`usePdfBlob`) and shows it in `DocumentPreview`;
 *   3. collects a drawn signature, a typed name (prefilled from `signerName`)
 *      and an intent affirmation in `SignatureForm`, then POSTs `/sign`.
 *      Success returns to the dashboard, whose refetch reflects the
 *      signature; a 401 signs out; a 409 or 422 returns to the dashboard.
 */
export function PortalSign({
  session,
  enrollmentId,
  onSignOut,
  onExit,
}: PortalSignProps): ReactNode {
  const [detail, setDetail] = useState<PortalEnrollmentDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load the enrollment detail and gate the view: only a signable, unsigned
  // form within this session's scope may be signed here.
  useEffect(() => {
    let cancelled = false;
    apiFetch<PortalEnrollmentDetail>(
      `/api/portal/enrollments/${enrollmentId}`,
      {},
      { baseUrl: API_BASE, token: session },
    )
      .then((d) => {
        if (cancelled) return;
        if (d.esignStatus === "signed" || !d.signable) {
          onExit();
          return;
        }
        setDetail(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 401) {
            onSignOut();
            return;
          }
          // A form outside the session's scope (the IDOR guard) or an unknown
          // id: the dashboard reflects reality, so send the member back rather
          // than dead-ending here.
          if (err.status === 404) {
            onExit();
            return;
          }
        }
        setLoadError("We couldn't load this form. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [enrollmentId, session, onSignOut, onExit]);

  // The PDF is fetched only once the detail load has passed the gate, so no
  // PDF is fetched for a form about to bounce away.
  const pdf = usePdfBlob(
    detail ? `/api/portal/enrollments/${enrollmentId}/sign-document` : null,
    session,
    onSignOut,
  );

  const onSubmit = ({ signaturePngBase64, typedName }: SignatureInput): void => {
    setBusy(true);
    setSubmitError(null);
    apiFetch<PortalEnrollmentDetail>(
      `/api/portal/enrollments/${enrollmentId}/sign`,
      {
        method: "POST",
        body: { signaturePngBase64, typedName, intent: true } satisfies PortalSignRequest,
      },
      { baseUrl: API_BASE, token: session },
    )
      .then(() => {
        onExit();
      })
      .catch((err: unknown) => {
        setBusy(false);
        if (err instanceof ApiError) {
          if (err.status === 401) {
            onSignOut();
            return;
          }
          // A 409 (already signed) or 422 (the form went incomplete, or the
          // intent or image was rejected): the refetched dashboard reflects
          // reality, so bounce back.
          if (err.status === 409 || err.status === 422) {
            onExit();
            return;
          }
        }
        setSubmitError("We couldn't record your signature. Please try again.");
      });
  };

  const error = loadError ?? (pdf.failed ? "We couldn't load your form. Please try again." : null);
  if (error) {
    return <Alert tone="error" title={error} />;
  }

  if (!detail || !pdf.doc) {
    return (
      <div className="text-slate-teal flex items-center gap-3">
        <Spinner /> <span>Loading your form…</span>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-h3 text-midnight font-serif">Review and sign</h1>
      <p className="text-body-sm text-slate-teal mt-2">
        Signing as{" "}
        <strong>{detail.signedAs === "caregiver" ? "caregiver" : "the participant"}</strong>. Please
        review the form below, then add your signature.
      </p>

      <DocumentPreview title={FORM_TITLE} doc={pdf.doc} filename="cms-10882.pdf" />

      <SignatureForm
        initialName={detail.signerName}
        intentLabel="I intend to sign this form electronically and agree to do business electronically."
        busy={busy}
        notice={submitError && <Alert tone="error" title={submitError} />}
        onSubmit={onSubmit}
      />
    </div>
  );
}
